<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { DocumentTextIcon, EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'

const FORM_FILTER_STORAGE_KEY = 'bs2-orders-form-filter'

interface FormOption {
  code: string
  label: string
}

const props = defineProps<{
  forms: FormOption[]
}>()

const emit = defineEmits<{
  formFilterChanged: [visibleCodes: string[]]
}>()

function loadFromStorage(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(FORM_FILTER_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

function saveToStorage(val: Record<string, boolean>) {
  try {
    localStorage.setItem(FORM_FILTER_STORAGE_KEY, JSON.stringify(val))
  } catch {
    // ignore
  }
}

const formVisibility = ref<Record<string, boolean>>({})

function setDefaultVisibility() {
  formVisibility.value = props.forms.reduce(
    (acc, f) => {
      acc[f.code] = true
      return acc
    },
    {} as Record<string, boolean>,
  )
}

const visibleFormCodes = computed(() =>
  Object.entries(formVisibility.value)
    .filter(([_, visible]) => visible)
    .map(([code]) => code),
)

watch(
  () => props.forms,
  (newForms) => {
    if (newForms.length > 0) {
      const saved = loadFromStorage()
      const merged = newForms.reduce(
        (acc, f) => {
          acc[f.code] = f.code in saved ? saved[f.code] : true
          return acc
        },
        {} as Record<string, boolean>,
      )
      formVisibility.value = merged
      emit('formFilterChanged', Object.entries(merged).filter(([, v]) => v).map(([c]) => c))
    }
  },
  { immediate: true, deep: true },
)

watch(
  formVisibility,
  (val) => {
    saveToStorage(val)
    emit('formFilterChanged', visibleFormCodes.value)
  },
  { deep: true },
)

function toggleForm(code: string) {
  formVisibility.value[code] = !formVisibility.value[code]
}

function showAll() {
  props.forms.forEach((f) => {
    formVisibility.value[f.code] = true
  })
}

function hideAll() {
  props.forms.forEach((f) => {
    formVisibility.value[f.code] = false
  })
}

function resetToDefault() {
  setDefaultVisibility()
}

</script>

<template>
  <div class="dropdown dropdown-end">
    <div tabindex="0" class="btn btn-ghost btn-sm gap-2" role="button">
      <DocumentTextIcon class="w-5 h-5" />
      <span class="hidden sm:inline">Formulaire visible</span>
    </div>
    <div tabindex="-1" class="dropdown-content z-[100] menu p-4 shadow-lg bg-base-100 rounded-box w-72 border border-base-300">
      <div class="flex justify-between items-center mb-3">
        <h3 class="font-semibold text-sm">Formulaires visibles</h3>
      </div>
      <p class="text-xs text-base-content/60 mb-2">Décochez les types de formulaires que vous ne souhaitez pas voir.</p>
      <div class="divider my-1"></div>

      <div class="max-h-64 overflow-y-auto space-y-1">
        <label
          v-for="form in forms"
          :key="form.code"
          class="flex items-center gap-2 p-2 rounded hover:bg-base-200 cursor-pointer transition-colors"
        >
          <input
            type="checkbox"
            class="checkbox checkbox-sm"
            :checked="formVisibility[form.code]"
            @change="toggleForm(form.code)"
          />
          <span class="text-sm flex-1">{{ form.label }}</span>
          <component :is="formVisibility[form.code] ? EyeIcon : EyeSlashIcon" class="w-4 h-4 opacity-50" />
        </label>
      </div>

      <div class="divider my-2"></div>

      <div class="flex gap-2 text-xs">
        <button class="btn btn-ghost btn-xs flex-1" @click="showAll" title="Tout afficher">Tout afficher</button>
        <button class="btn btn-ghost btn-xs flex-1" @click="hideAll" title="Tout masquer">Tout masquer</button>
        <button class="btn btn-ghost btn-xs flex-1" @click="resetToDefault" title="Réinitialiser">Réinitialiser</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dropdown-content {
  margin-top: 8px;
}
</style>
